// frontend/src/pages/VersePage.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sanscript from '@indic-transliteration/sanscript';
import ApiService from '../services/ApiService';
import { useSetting } from '../hooks/useSettings';
import styles from './VersePage.module.css';

const TELUGU_SOURCES = [
  'VEMANA', 'NANNAYYA', 'POTANA', 'TIKKANA',
  'ERRAPRAGADA', 'SUMATI', 'DASARATHI',
  'ANNAMACHARYA', 'TYAGARAJA', 'POTHULURI',
];

const SCRIPTS = [
  { value: 'devanagari', label: '\u0926\u0947\u0935' },
  { value: 'iast',       label: 'IAST' },
  { value: 'telugu',     label: '\u0c24\u0c46\u0c32\u0c41' },
];

function getSourceCode(verse) {
  return (verse.source_code || verse.verse_id || '').split('#')[0].toUpperCase();
}

function isTeluguVerse(verse) {
  if (verse.language === 'te') return true;
  return TELUGU_SOURCES.includes(getSourceCode(verse));
}

function toDevanagari(iast) {
  if (!iast) return '';
  try { return Sanscript.t(iast, 'iast', 'devanagari'); }
  catch { return iast; }
}

export default function VersePage() {
  const { verseId } = useParams();
  const navigate    = useNavigate();
  const [verse, setVerse]   = useState(null);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);
  const [script, setScript] = useSetting('defaultScript');

  useEffect(() => {
    setLoad(true);
    setError(null);
    ApiService.getVerse(decodeURIComponent(verseId))
      .then(setVerse)
      .catch(e => setError(e.message))
      .finally(() => setLoad(false));
  }, [verseId]);

  if (loading) return (
    <div className={styles.center}>
      <span aria-label="Loading">⟳</span>
    </div>
  );

  if (error) return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => navigate(-1)}>← Back</button>
      <div className={`${styles.center} ${styles.errorMsg}`}>
        Could not load verse: {error}
      </div>
    </div>
  );

  if (!verse) return null;

  const telugu = isTeluguVerse(verse);

  // Resolve display text based on script toggle
  let displayText = '';
  let displayClass = styles.originalLarge;

  if (telugu) {
    displayText  = verse.original_text || verse.telugu_text || verse.iast_text || '';
    displayClass = styles.teluguLarge;
  } else if (script === 'iast') {
    displayText  = verse.iast_text || '';
    displayClass = styles.iastLarge;
  } else {
    // devanagari (default) — use stored or auto-convert
    displayText  = verse.original_text || toDevanagari(verse.iast_text);
    displayClass = styles.originalLarge;
  }

  const showIastSecondary = !telugu && script !== 'iast' && verse.iast_text;

  const scorePercent = verse.semantic_score
    ? Math.round(verse.semantic_score * 100)
    : null;

  return (
    <div className={styles.page}>

      {/* Back + script switcher */}
      <div className={styles.scriptToggle}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>← Back</button>
        {!telugu && (
          <div role="group" aria-label="Script display">
            {SCRIPTS.filter(s => s.value !== 'telugu').map(s => (
              <button
                key={s.value}
                className={`${styles.scriptBtn} ${script === s.value ? styles.scriptBtnActive : ''}`}
                onClick={() => setScript(s.value)}
                aria-pressed={script === s.value}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Primary verse card */}
      <div className={styles.verseCard}>
        <p className={displayClass}>{displayText}</p>

        {/* IAST secondary below Devanagari */}
        {showIastSecondary && (
          <p className={styles.iastDisplay}>{verse.iast_text}</p>
        )}

        <div className={styles.divider} />

        {verse.english_translation && (
          <p className={styles.translationDisplay}>{verse.english_translation}</p>
        )}
      </div>

      {/* Metadata */}
      <div className={styles.metaCard}>
        <div className={styles.metaGrid}>
          <div className={styles.metaItem}>
            <span className={styles.metaKey}>Source</span>
            <span className={styles.metaVal}>{verse.source_title}</span>
          </div>
          {verse.chapter && verse.verse_number && (
            <div className={styles.metaItem}>
              <span className={styles.metaKey}>Reference</span>
              <span className={styles.metaVal}>{verse.chapter}.{verse.verse_number}</span>
            </div>
          )}
          <div className={styles.metaItem}>
            <span className={styles.metaKey}>Tradition</span>
            <span className="tag tag--tradition">{verse.tradition}</span>
          </div>
          {scorePercent !== null && (
            <div className={styles.metaItem}>
              <span className={styles.metaKey}>Relevance</span>
              <div className={styles.scoreWrap}>
                <span className="score-badge">{verse.semantic_score.toFixed(2)}</span>
                <div className={styles.scoreBar}>
                  <div className={styles.scoreFill} style={{ width: `${scorePercent}%` }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tags */}
      {(verse.tags || []).length > 0 && (
        <section className={styles.tagsSection}>
          <div className={styles.sectionLabel}>TAGS</div>
          <div className={styles.tags}>
            {verse.tags.map(tag => (
              <span key={tag} className="tag tag--neutral">{tag}</span>
            ))}
          </div>
        </section>
      )}

      {/* Commentary */}
      {verse.commentary && (
        <section className={styles.relatedSection}>
          <div className={styles.sectionLabel}>COMMENTARY</div>
          <div className={styles.metaCard}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontStyle: 'italic',
                        color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              {verse.commentary}
            </p>
          </div>
        </section>
      )}

    </div>
  );
}
